(() => {
  const isDesktop = () => window.matchMedia('(min-width: 990px)').matches;

  // Swap [data-src] -> [src] on a video's <source> children, then call load().
  // The Liquid template ships the URLs in data-src + preload="none" so nothing
  // is fetched on page load; we only kick off the network request once the
  // user actually needs the video (hover on desktop, in-view on mobile).
  const ensureLoaded = (video) => {
    if (!video || video.dataset.vwLoaded === 'true') return;
    let armed = false;
    video.querySelectorAll('source[data-src]').forEach((s) => {
      const u = s.getAttribute('data-src');
      if (!u) return;
      s.setAttribute('src', u);
      s.removeAttribute('data-src');
      armed = true;
    });
    if (armed) {
      video.dataset.vwLoaded = 'true';
      try { video.load(); } catch (_) {}
    }
  };

  const sections = document.querySelectorAll('.video-ways');
  sections.forEach((section) => {
    /* === Mobile carousel pagination dots === */
    const rail = section.querySelector('[data-vw-rail]');
    const dots = section.querySelectorAll('[data-vw-dots] [data-vw-dot]');
    const cards = rail ? rail.querySelectorAll('.vw-card') : [];

    if (rail && dots.length && cards.length) {
      dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const idx = parseInt(dot.dataset.vwDot, 10);
          const card = cards[idx];
          if (card) card.scrollIntoView({ inline: 'start', block: 'nearest', behavior: 'smooth' });
        });
      });

      if ('IntersectionObserver' in window) {
        const visibleSet = new Set();
        let sectionInView = false;

        // Pause any playing card video — used when section leaves viewport.
        const pauseAll = () => {
          cards.forEach((c) => {
            const vv = c.querySelector('[data-vw-video]');
            if (vv) {
              try { vv.pause(); } catch (_) {}
              vv.muted = true;
              c.classList.remove('vw-card--playing');
            }
          });
        };

        // Play only the dominant visible card on mobile; pause all others.
        const syncMobileAutoplay = () => {
          if (isDesktop()) return;
          if (!visibleSet.size || !sectionInView) {
            pauseAll();
            return;
          }
          const dominant = Math.min(...visibleSet);
          cards.forEach((c, i) => {
            const v = c.querySelector('[data-vw-video]');
            if (!v) return;
            if (i === dominant) {
              if (v.paused) {
                ensureLoaded(v);
                v.muted = true;
                c.classList.add('vw-card--playing');
                const p = v.play();
                if (p && typeof p.catch === 'function') p.catch(() => {});
              }
            } else if (!v.paused) {
              try { v.pause(); } catch (_) {}
              v.currentTime = 0;
              c.classList.remove('vw-card--playing');
            }
          });
        };

        const cardIO = new IntersectionObserver(
          (entries) => {
            entries.forEach((e) => {
              const idx = Array.from(cards).indexOf(e.target);
              if (e.isIntersecting && e.intersectionRatio > 0.6) visibleSet.add(idx);
              else visibleSet.delete(idx);
            });
            if (visibleSet.size) {
              const first = Math.min(...visibleSet);
              dots.forEach((d, i) => d.classList.toggle('video-ways__dot--active', i === first));
            }
            syncMobileAutoplay();
          },
          { root: rail, threshold: [0, 0.6] }
        );
        cards.forEach((c) => cardIO.observe(c));

        // Section-level visibility: only autoplay when the rail itself is on screen.
        const sectionIO = new IntersectionObserver(
          (entries) => {
            entries.forEach((e) => {
              sectionInView = e.isIntersecting && e.intersectionRatio >= 0.25;
              syncMobileAutoplay();
            });
          },
          { threshold: [0, 0.25, 0.6] }
        );
        sectionIO.observe(section);

        document.addEventListener('visibilitychange', () => {
          if (document.hidden) pauseAll();
          else syncMobileAutoplay();
        });
      }
    }

    /* === Desktop hover-to-play + click-to-zoom === */
    if (!rail) return;

    const autoplay = rail.dataset.vwAutoplay === 'true';
    const zoomEnabled = rail.dataset.vwZoom === 'true';
    const overlay = section.querySelector('[data-vw-zoom-overlay]');
    const player = section.querySelector('[data-vw-zoom-player]');
    const closeBtn = section.querySelector('[data-vw-zoom-close]');
    const backdrop = section.querySelector('[data-vw-zoom-backdrop]');

    cards.forEach((card) => {
      const video = card.querySelector('[data-vw-video]');
      if (!video) return;

      // Hover preview
      card.addEventListener('mouseenter', () => {
        if (!autoplay || !isDesktop()) return;
        ensureLoaded(video);
        card.classList.add('vw-card--playing');
        video.currentTime = 0;
        const p = video.play();
        if (p && typeof p.catch === 'function') p.catch(() => {});
      });
      card.addEventListener('mouseleave', () => {
        if (!isDesktop()) return;
        card.classList.remove('vw-card--playing');
        video.pause();
        video.currentTime = 0;
      });

      // Click → open zoom
      const link = card.querySelector('.vw-card__link');
      if (link && zoomEnabled && overlay && player) {
        const openZoom = (e) => {
          if (!isDesktop()) return; // mobile: let default behavior run
          e.preventDefault();

          // Pause inline preview
          card.classList.remove('vw-card--playing');
          video.pause();

          // Make sure the original has src wired up before we clone it.
          ensureLoaded(video);

          // Clone the video into the overlay player
          player.innerHTML = '';
          const big = video.cloneNode(true);
          big.removeAttribute('muted');
          big.muted = false;
          big.controls = true;
          big.autoplay = true;
          big.loop = false;
          player.appendChild(big);

          overlay.hidden = false;
          overlay.setAttribute('data-vw-open', '');
          document.body.classList.add('vw-zoom-open');
          big.play().catch(() => {});
        };

        link.addEventListener('click', openZoom);
        link.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') openZoom(e);
        });
      }
    });

    /* Close zoom */
    const closeZoom = () => {
      if (!overlay || !player) return;
      const big = player.querySelector('video, iframe');
      if (big && big.pause) big.pause();
      player.innerHTML = '';
      overlay.removeAttribute('data-vw-open');
      overlay.hidden = true;
      document.body.classList.remove('vw-zoom-open');
    };

    if (closeBtn) closeBtn.addEventListener('click', closeZoom);
    if (backdrop) backdrop.addEventListener('click', closeZoom);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && overlay && overlay.hasAttribute('data-vw-open')) closeZoom();
    });
  });
})();
